import * as yup from 'yup'
import { defineMessages, IntlShape } from 'react-intl'
import { INewBusinessClientFormValues } from 'features/BusinessClients/types'
import schemaValidations from 'utils/schema'
import { getDifference } from 'utils/object'
import { isEmpty } from 'lodash'

const messages = defineMessages({
  required: {
    id: 'businessClients.new.form.required',
    defaultMessage: 'This field is required'
  },
  max: {
    id: 'businessClients.new.form.max',
    defaultMessage: 'This field must be at most {max} characters'
  },
  email: {
    id: 'businessClients.new.form.email',
    defaultMessage: 'This field must be a valid email'
  }
})

export const newBusinessClientSchema = (
  intl: IntlShape,
  initialValues: INewBusinessClientFormValues
): yup.SchemaOf<Omit<INewBusinessClientFormValues, 'logoFile'>> => {
  const contactSchema = yup.object().shape({
    id: yup.string(),
    name: yup
      .string()
      .required(intl.formatMessage(messages.required))
      .max(50, intl.formatMessage(messages.max, { max: 50 })),
    phone: schemaValidations(intl).phone.required,
    secondaryPhone: schemaValidations(intl).phone.optional,
    email: yup
      .string()
      .required(intl.formatMessage(messages.required))
      .email(intl.formatMessage(messages.email))
      .max(50, intl.formatMessage(messages.max, { max: 50 })),
    secondaryEmail: yup
      .string()
      .email(intl.formatMessage(messages.email))
      .max(50, intl.formatMessage(messages.max, { max: 50 }))
  })

  return yup.object().shape({
    physicalSims: yup.array(),
    credits: yup.array(),
    kreditsSettings: yup.object().when('$kreditsSettings', {
      is: (value: INewBusinessClientFormValues['kreditsSettings']) => {
        value.categories = value.categories.map((category) => ({
          ...category,
          amount: category.amount ?? '',
          name: category.name ?? ''
        }))
        const kreditsSettingsPayload = getDifference(value, initialValues.kreditsSettings)

        return !isEmpty(kreditsSettingsPayload)
      },
      then: yup
        .object()
        .shape({
          sites: yup.array().when('isAllSites', {
            is: false,
            then: yup.array().min(1, intl.formatMessage(messages.required)),
            otherwise: yup.array().notRequired()
          }),
          categories: yup.array().of(
            yup.object().shape({
              name: yup.string().required(intl.formatMessage(messages.required)),
              frequency: yup.object().shape({
                value: yup.string().required(intl.formatMessage(messages.required)),
                unit: yup.string().required(intl.formatMessage(messages.required))
              }),
              amount: yup.string().required(intl.formatMessage(messages.required))
            })
          )
        })
        .required(intl.formatMessage(messages.required)),
      otherwise: yup.object()
    }) as yup.SchemaOf<INewBusinessClientFormValues['kreditsSettings']>,
    verticals: yup.array().of(yup.string()).notRequired(),
    logo: yup.mixed().optional(),
    clientName: yup
      .string()
      .required(intl.formatMessage(messages.required))
      .max(30, intl.formatMessage(messages.max, { max: 30 })),
    emailDomains: yup.array().of(yup.string()).notRequired(),
    shareCustomersData: yup.boolean().optional(),
    clientContacts: yup.array().of(contactSchema).min(1, intl.formatMessage(messages.required)),
    kam: yup.array().nullable().of(yup.string()).notRequired(),
    companies: (yup.array() as yup.SchemaOf<INewBusinessClientFormValues['companies']>).min(
      1,
      intl.formatMessage(messages.required)
    )
  })
}
